import React, { useState, useEffect, useRef, useCallback } from 'react';

// --- Constants ---
const GAME_WIDTH = 500; // Canvas width - Made wider
const GAME_HEIGHT = 650; // Canvas height - Slightly taller

// Road properties
const LANE_COUNT = 3;
const ROAD_LINE_WIDTH = 5;
const ROAD_LINE_DASH = 30;
const ROAD_LINE_GAP = 30;

// Player Car properties
const PLAYER_CAR_WIDTH = 60; // Slightly wider car
const PLAYER_CAR_HEIGHT = 100; // Slightly taller car
const PLAYER_CAR_INITIAL_X = GAME_WIDTH / 2 - PLAYER_CAR_WIDTH / 2;
const PLAYER_CAR_Y = GAME_HEIGHT * 0.8; // Player car fixed at bottom of screen

// Player Car physics
const INITIAL_SPEED = 10; // Pixels per frame (base scrolling speed)
const MAX_SPEED = 30; // Increased max speed
const ACCELERATION_RATE = 0.6;
const BRAKING_RATE = 1.2;
const FRICTION = 0.97; // Reduces speed over time
const STEER_SPEED = 10; // Lateral movement speed (pixels per frame)

// Game Mechanics
const LIVES_START = 3;
const INVINCIBILITY_DURATION = 120; // Frames (e.g., 2 seconds at 60fps)
const DIFFICULTY_SCORE_INTERVAL = 1500; // Score interval to increase difficulty
const SPEED_INCREASE_PER_DIFFICULTY = 2; // Increase MAX_SPEED
const SPAWN_INTERVAL_DECREASE_PER_DIFFICULTY = 70; // Decrease spawn interval
const MIN_SPAWN_INTERVAL = 300; // Minimum spawn interval for obstacles

const FUEL_MAX = 100;
const FUEL_DEPLETION_RATE = 0.05; // Fuel consumed per frame
const FUEL_BOOST_REFILL = 20; // Fuel refilled by boost item

const SCORE_MULTIPLIER_BASE = 1;
const SCORE_MULTIPLIER_SPEED_THRESHOLD = MAX_SPEED * 0.7; // Speed to get multiplier
const SCORE_MULTIPLIER_INCREASE = 0.1;
const SCORE_MULTIPLIER_DECAY = 0.005;

// Obstacle properties
const OBSTACLE_WIDTH = 60;
const OBSTACLE_HEIGHT = 90;
const OBSTACLE_SPAWN_INTERVAL = 700; // milliseconds
const OBSTACLE_MAX_COUNT = 10; // Max number of obstacles on screen
const OBSTACLE_COLORS = ['#555', '#777', '#999', '#3a3a3a', '#4b0082', '#ff6347', '#4682b4', '#8a2be2']; // More varied car colors

// Boost properties
const BOOST_SIZE = 45; // Slightly larger boost
const BOOST_SPAWN_INTERVAL = 2000; // milliseconds (more frequent)
const BOOST_EFFECT_SPEED_INCREASE = 7; // More speed from boost
const BOOST_SCORE_BONUS = 200; // More score from boost

// Game states
const GAME_STATE = {
  MENU: 'MENU',
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  GAME_OVER: 'GAME_OVER',
};

// --- Helper Functions for Drawing ---

// Draws a rectangle
const drawRect = (ctx, x, y, width, height, color) => {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, width, height);
};

// Draws a rounded rectangle
const drawRoundedRect = (ctx, x, y, width, height, radius, color) => {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  ctx.fill();
};

// Draws a circle
const drawCircle = (ctx, x, y, radius, color) => {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
};

// Draws a triangle (for car details)
const drawTriangle = (ctx, x1, y1, x2, y2, x3, y3, color) => {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x3, y3);
  ctx.closePath();
  ctx.fill();
};

// Draws an advanced car shape with gradients and more detail
const drawCar = (ctx, x, y, width, height, baseColor, isPlayer = false, speed = 0) => {
  const bodyHeight = height * 0.8;
  const roofHeight = height * 0.3;
  const wheelRadius = width * 0.1;

  // Create gradient for car body for a sleek look
  const gradient = ctx.createLinearGradient(x, y, x + width, y + height);
  gradient.addColorStop(0, baseColor);
  gradient.addColorStop(0.5, isPlayer ? '#FF4500' : '#444'); // Player is brighter red
  gradient.addColorStop(1, baseColor);

  // Car body
  ctx.fillStyle = gradient;
  drawRoundedRect(ctx, x, y + height - bodyHeight, width, bodyHeight, width * 0.15, gradient);

  // Roof / Cabin
  ctx.fillStyle = baseColor;
  drawRect(ctx, x + width * 0.2, y + height - bodyHeight - roofHeight, width * 0.6, roofHeight, baseColor);

  // Windshield
  ctx.fillStyle = 'rgba(100, 200, 255, 0.7)';
  ctx.fillRect(x + width * 0.25, y + height - bodyHeight - roofHeight * 0.9, width * 0.5, roofHeight * 0.7);

  // Wheels (Darker grey with a slight inner circle for hubcap)
  drawCircle(ctx, x + width * 0.15, y + height - wheelRadius, wheelRadius, '#222');
  drawCircle(ctx, x + width * 0.15, y + height - wheelRadius, wheelRadius * 0.6, '#444');
  drawCircle(ctx, x + width * 0.85, y + height - wheelRadius, wheelRadius, '#222');
  drawCircle(ctx, x + width * 0.85, y + height - wheelRadius, wheelRadius * 0.6, '#444');


  if (isPlayer) {
    // Advanced Headlights (glowing effect)
    ctx.save();
    ctx.shadowBlur = 10;
    ctx.shadowColor = 'yellow';
    drawRect(ctx, x + width * 0.1, y + height - width * 0.15, width * 0.15, width * 0.08, 'yellow');
    drawRect(ctx, x + width * 0.75, y + height - width * 0.15, width * 0.15, width * 0.08, 'yellow');
    ctx.restore();

    // Spoiler (more pronounced)
    drawTriangle(ctx, x + width * 0.15, y + height * 0.15, x + width * 0.85, y + height * 0.15, x + width * 0.5, y + height * 0.08, '#666');
    drawRect(ctx, x + width * 0.15, y + height * 0.18, width * 0.05, height * 0.05, '#666'); // Left support
    drawRect(ctx, x + width * 0.80, y + height * 0.18, width * 0.05, height * 0.05, '#666'); // Right support

    // Exhaust glow (more visible at higher speeds)
    if (speed > INITIAL_SPEED * 1.5) {
      const glowAlpha = Math.min(1, (speed - INITIAL_SPEED * 1.5) / (MAX_SPEED - INITIAL_SPEED * 1.5));
      ctx.save();
      ctx.shadowBlur = 15;
      ctx.shadowColor = `rgba(255, 100, 0, ${glowAlpha})`; // Orange glow
      ctx.fillStyle = `rgba(255, 140, 0, ${glowAlpha})`;
      drawCircle(ctx, x + width * 0.25, y + height * 0.95, 5, `rgba(255, 140, 0, ${glowAlpha})`);
      drawCircle(ctx, x + width * 0.75, y + height * 0.95, 5, `rgba(255, 140, 0, ${glowAlpha})`);
      ctx.restore();
    }

  } else {
    // Taillights
    drawRect(ctx, x + width * 0.1, y + height - width * 0.15, width * 0.15, width * 0.08, 'red');
    drawRect(ctx, x + width * 0.75, y + height - width * 0.15, width * 0.15, width * 0.08, 'red');
  }
};


// --- Game Component ---
const RacingGame = () => {
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);
  const keysPressed = useRef({});

  // Game State
  const [gameState, setGameState] = useState(GAME_STATE.MENU);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [lives, setLives] = useState(LIVES_START);
  const [fuel, setFuel] = useState(FUEL_MAX); // New feature: Fuel
  const [scoreMultiplier, setScoreMultiplier] = useState(SCORE_MULTIPLIER_BASE); // New feature: Multiplier

  const invincibleTimer = useRef(0); // Frames until invincibility ends
  const currentMaxSpeed = useRef(MAX_SPEED);
  const currentObstacleSpawnInterval = useRef(OBSTACLE_SPAWN_INTERVAL);
  const currentBoostSpawnInterval = useRef(BOOST_SPAWN_INTERVAL);

  // Player Car State
  const playerCar = useRef({
    x: PLAYER_CAR_INITIAL_X,
    speed: 0, // Current forward speed (for scrolling background)
  });

  // Dynamic Game Elements
  const obstacles = useRef([]);
  const boosts = useRef([]);
  const roadLines = useRef([]); // For scrolling effect
  const lastObstacleSpawnTime = useRef(0);
  const lastBoostSpawnTime = useRef(0);

  // --- Game Initialization ---
  const initializeGame = useCallback(() => {
    setScore(0);
    setLives(LIVES_START);
    setFuel(FUEL_MAX); // Reset fuel
    setScoreMultiplier(SCORE_MULTIPLIER_BASE); // Reset multiplier
    invincibleTimer.current = 0;
    currentMaxSpeed.current = MAX_SPEED;
    currentObstacleSpawnInterval.current = OBSTACLE_SPAWN_INTERVAL;
    currentBoostSpawnInterval.current = BOOST_SPAWN_INTERVAL;

    playerCar.current = {
      x: PLAYER_CAR_INITIAL_X,
      speed: INITIAL_SPEED,
    };
    obstacles.current = [];
    boosts.current = [];
    roadLines.current = [];
    lastObstacleSpawnTime.current = performance.now();
    lastBoostSpawnTime.current = performance.now();

    // Initialize road lines for scrolling effect
    for (let i = 0; i < (GAME_HEIGHT / (ROAD_LINE_DASH + ROAD_LINE_GAP)) + 2; i++) {
      roadLines.current.push({
        y: i * (ROAD_LINE_DASH + ROAD_LINE_GAP) - ROAD_LINE_DASH, // Initial positions
      });
    }

    setGameState(GAME_STATE.PLAYING);
  }, []);

  // --- Keyboard Input Handling ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      keysPressed.current[e.key] = true;
      if (e.key === 'Escape' && gameState === GAME_STATE.PLAYING) {
        setGameState(GAME_STATE.PAUSED);
      } else if (e.key === 'Escape' && gameState === GAME_STATE.PAUSED) {
        setGameState(GAME_STATE.PLAYING);
      }
    };
    const handleKeyUp = (e) => {
      keysPressed.current[e.key] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState]); // Add gameState as dependency for pause key handler

  // --- Game Loop ---
  const gameLoop = useCallback(() => {
    if (gameState !== GAME_STATE.PLAYING) {
      cancelAnimationFrame(animationFrameId.current);
      return;
    }

    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // --- Update Player Physics ---
    let currentSpeed = playerCar.current.speed;

    if (keysPressed.current['ArrowUp'] || keysPressed.current['w'] || keysPressed.current['W']) {
      currentSpeed += ACCELERATION_RATE;
    } else if (keysPressed.current['ArrowDown'] || keysPressed.current['s'] || keysPressed.current['S']) {
      currentSpeed -= BRAKING_RATE;
    } else {
      currentSpeed *= FRICTION; // Apply friction if no input
    }

    currentSpeed = Math.max(INITIAL_SPEED / 2, Math.min(currentSpeed, currentMaxSpeed.current)); // Clamp speed
    playerCar.current.speed = currentSpeed;

    // Update player's lateral position based on steering
    if (keysPressed.current['ArrowLeft'] || keysPressed.current['a'] || keysPressed.current['A']) {
      playerCar.current.x -= STEER_SPEED;
    }
    if (keysPressed.current['ArrowRight'] || keysPressed.current['d'] || keysPressed.current['D']) {
      playerCar.current.x += STEER_SPEED;
    }

    // Clamp player's lateral position to stay within road bounds
    playerCar.current.x = Math.max(0, Math.min(GAME_WIDTH - PLAYER_CAR_WIDTH, playerCar.current.x));

    // --- Fuel Depletion ---
    setFuel(prev => Math.max(0, prev - FUEL_DEPLETION_RATE * (currentSpeed / INITIAL_SPEED))); // Depletes faster at higher speed
    if (fuel <= 0) {
      setGameState(GAME_STATE.GAME_OVER);
    }

    // --- Score Multiplier Update ---
    setScoreMultiplier(prev => {
      if (playerCar.current.speed >= SCORE_MULTIPLIER_SPEED_THRESHOLD) {
        return Math.min(3, prev + SCORE_MULTIPLIER_INCREASE); // Max multiplier of 3
      } else {
        return Math.max(SCORE_MULTIPLIER_BASE, prev - SCORE_MULTIPLIER_DECAY);
      }
    });

    // Update score
    setScore(prev => prev + Math.floor((currentSpeed / 10) * scoreMultiplier)); // Score based on speed and multiplier

    // --- Invincibility Timer ---
    if (invincibleTimer.current > 0) {
      invincibleTimer.current--;
    }

    // --- Difficulty Scaling ---
    const currentDifficultyLevel = Math.floor(score / DIFFICULTY_SCORE_INTERVAL);
    if (currentDifficultyLevel > 0 && currentDifficultyLevel > Math.floor((score - Math.floor((currentSpeed / 10) * scoreMultiplier)) / DIFFICULTY_SCORE_INTERVAL)) { // Check if crossed new interval
      currentMaxSpeed.current += SPEED_INCREASE_PER_DIFFICULTY;
      currentObstacleSpawnInterval.current = Math.max(MIN_SPAWN_INTERVAL, currentObstacleSpawnInterval.current - SPAWN_INTERVAL_DECREASE_PER_DIFFICULTY);
      currentBoostSpawnInterval.current = Math.max(MIN_SPAWN_INTERVAL * 2, currentBoostSpawnInterval.current - SPAWN_INTERVAL_DECREASE_PER_DIFFICULTY * 2);
    }


    // --- Update Road Lines (Scrolling) ---
    roadLines.current = roadLines.current.map(line => {
      line.y += currentSpeed; // Move lines down based on player speed
      if (line.y > GAME_HEIGHT) {
        line.y -= (ROAD_LINE_DASH + ROAD_LINE_GAP) * roadLines.current.length; // Wrap around
      }
      return line;
    });

    // --- Update Obstacles ---
    obstacles.current = obstacles.current
      .map(obs => ({ ...obs, y: obs.y + currentSpeed }))
      .filter(obs => obs.y < GAME_HEIGHT + OBSTACLE_HEIGHT); // Remove when off screen

    const currentTime = performance.now();
    if (currentTime - lastObstacleSpawnTime.current > currentObstacleSpawnInterval.current && obstacles.current.length < OBSTACLE_MAX_COUNT) {
      const xPos = Math.random() * (GAME_WIDTH - OBSTACLE_WIDTH); // Random X across full width
      const color = OBSTACLE_COLORS[Math.floor(Math.random() * OBSTACLE_COLORS.length)];
      obstacles.current.push({
        x: xPos,
        y: -OBSTACLE_HEIGHT, // Spawn off-screen top
        color: color,
      });
      lastObstacleSpawnTime.current = currentTime;
    }

    // --- Update Boosts ---
    boosts.current = boosts.current
      .map(boost => ({ ...boost, y: boost.y + currentSpeed }))
      .filter(boost => boost.y < GAME_HEIGHT + BOOST_SIZE);

    if (currentTime - lastBoostSpawnTime.current > currentBoostSpawnInterval.current) {
      const xPos = Math.random() * (GAME_WIDTH - BOOST_SIZE); // Random X across full width
      boosts.current.push({
        x: xPos,
        y: -BOOST_SIZE, // Spawn off-screen top
      });
      lastBoostSpawnTime.current = currentTime;
    }

    // --- Collision Detection ---
    const playerBox = {
      x: playerCar.current.x,
      y: PLAYER_CAR_Y,
      width: PLAYER_CAR_WIDTH,
      height: PLAYER_CAR_HEIGHT,
    };

    // Obstacle collision
    obstacles.current = obstacles.current.filter(obs => {
      const obsBox = {
        x: obs.x,
        y: obs.y,
        width: OBSTACLE_WIDTH,
        height: OBSTACLE_HEIGHT,
      };

      if (!invincibleTimer.current && playerBox.x < obsBox.x + obsBox.width &&
          playerBox.x + playerBox.width > obsBox.x &&
          playerBox.y < obsBox.y + obsBox.height &&
          playerBox.y + playerBox.height > obsBox.y) {
        setLives(prev => prev - 1);
        invincibleTimer.current = INVINCIBILITY_DURATION; // Start invincibility
        if (lives - 1 <= 0) {
          setGameState(GAME_STATE.GAME_OVER);
        }
        return false; // Remove collided obstacle
      }
      return true; // Keep non-collided obstacles
    });

    // Boost collision
    boosts.current = boosts.current.filter(boost => {
      const boostBox = {
        x: boost.x,
        y: boost.y,
        width: BOOST_SIZE,
        height: BOOST_SIZE,
      };

      if (playerBox.x < boostBox.x + boostBox.width &&
          playerBox.x + playerBox.width > boostBox.x &&
          playerBox.y < boostBox.y + boostBox.height &&
          playerBox.y + playerBox.height > boostBox.y) {
        playerCar.current.speed = Math.min(currentMaxSpeed.current, playerCar.current.speed + BOOST_EFFECT_SPEED_INCREASE); // Increase speed on boost
        setScore(prev => prev + BOOST_SCORE_BONUS); // Bonus score for boost
        setFuel(prev => Math.min(FUEL_MAX, prev + FUEL_BOOST_REFILL)); // Refill fuel
        return false; // Remove collected boost
      }
      return true; // Keep non-collided boosts
    });

    // --- Drawing ---

    // Draw Background (Road)
    drawRect(ctx, 0, 0, GAME_WIDTH, GAME_HEIGHT, '#333'); // Dark grey road background

    // Draw Lane Markings
    ctx.strokeStyle = '#F0D000'; // Yellow
    ctx.lineWidth = ROAD_LINE_WIDTH;
    ctx.setLineDash([ROAD_LINE_DASH, ROAD_LINE_GAP]); // Dashed lines
    for (let i = 1; i < LANE_COUNT; i++) {
      const laneX = (i * GAME_WIDTH) / LANE_COUNT;
      roadLines.current.forEach(line => {
        ctx.beginPath();
        ctx.moveTo(laneX, line.y);
        ctx.lineTo(laneX, line.y + ROAD_LINE_DASH);
        ctx.stroke();
      });
    }
    ctx.setLineDash([]); // Reset line dash

    // Draw Obstacles
    obstacles.current.forEach(obs => {
      drawCar(ctx, obs.x, obs.y, OBSTACLE_WIDTH, OBSTACLE_HEIGHT, obs.color, false, playerCar.current.speed); // Pass speed for exhaust glow
    });

    // Draw Boosts
    boosts.current.forEach(boost => {
      ctx.save();
      ctx.shadowBlur = 15;
      ctx.shadowColor = 'rgba(255, 255, 0, 0.8)'; // Yellow glow
      drawCircle(ctx, boost.x + BOOST_SIZE / 2, boost.y + BOOST_SIZE / 2, BOOST_SIZE / 2, 'gold');
      ctx.restore();

      ctx.fillStyle = 'purple';
      ctx.font = `${BOOST_SIZE * 0.7}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⚡', boost.x + BOOST_SIZE / 2, boost.y + BOOST_SIZE / 2 + 2);
    });

    // Draw Player Car (with invincibility flicker)
    ctx.save();
    if (invincibleTimer.current > 0 && Math.floor(invincibleTimer.current / 10) % 2 === 0) {
      ctx.globalAlpha = 0.5; // Flicker effect
    }
    drawCar(ctx, playerCar.current.x, PLAYER_CAR_Y, PLAYER_CAR_WIDTH, PLAYER_CAR_HEIGHT, '#FF0000', true, playerCar.current.speed);
    ctx.restore();

    // --- Mini-map (New Feature) ---
    const miniMapSize = 120;
    const miniMapPadding = 10;
    const miniMapX = GAME_WIDTH - miniMapSize - miniMapPadding;
    const miniMapY = GAME_HEIGHT - miniMapSize - miniMapPadding;

    // Draw minimap background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    drawRoundedRect(ctx, miniMapX, miniMapY, miniMapSize, miniMapSize, 8, 'rgba(0,0,0,0.7)');
    ctx.strokeStyle = '#00FFFF'; // Cyan border
    ctx.lineWidth = 2;
    ctx.strokeRect(miniMapX, miniMapY, miniMapSize, miniMapSize);

    // Draw player on minimap
    const playerMapX = miniMapX + (playerCar.current.x / GAME_WIDTH) * miniMapSize;
    const playerMapY = miniMapY + (PLAYER_CAR_Y / GAME_HEIGHT) * miniMapSize;
    drawCircle(ctx, playerMapX, playerMapY, 5, 'red'); // Player dot

    // Draw obstacles on minimap
    obstacles.current.forEach(obs => {
      const obsMapX = miniMapX + (obs.x / GAME_WIDTH) * miniMapSize;
      const obsMapY = miniMapY + (obs.y / GAME_HEIGHT) * miniMapSize;
      drawRect(ctx, obsMapX - 2, obsMapY - 2, 4, 4, 'yellow'); // Obstacle dot
    });

    // Request next frame
    animationFrameId.current = requestAnimationFrame(gameLoop);
  }, [gameState, initializeGame, lives, score, fuel, scoreMultiplier]); // Added new states to dependency array

  // --- Game State Transitions (Effects) ---
  useEffect(() => {
    if (gameState === GAME_STATE.PLAYING) {
      animationFrameId.current = requestAnimationFrame(gameLoop);
    } else if (gameState === GAME_STATE.GAME_OVER) {
      setHighScore(prev => Math.max(prev, score));
      cancelAnimationFrame(animationFrameId.current);
    } else if (gameState === GAME_STATE.MENU || gameState === GAME_STATE.PAUSED) {
      cancelAnimationFrame(animationFrameId.current);
    }

    return () => {
      cancelAnimationFrame(animationFrameId.current);
    };
  }, [gameState, gameLoop, score]); // Added score to dependency for high score update

  // --- Touch Control Handlers ---
  const handleTouchMove = useCallback((direction) => {
    if (gameState !== GAME_STATE.PLAYING) return;
    if (direction === 'left') {
      playerCar.current.x -= STEER_SPEED * 1.5; // Slightly faster touch steer
    } else if (direction === 'right') {
      playerCar.current.x += STEER_SPEED * 1.5;
    } else if (direction === 'up') {
      playerCar.current.speed = Math.min(currentMaxSpeed.current, playerCar.current.speed + ACCELERATION_RATE * 2);
    } else if (direction === 'down') {
      playerCar.current.speed = Math.max(0, playerCar.current.speed - BRAKING_RATE * 2);
    }
    // Clamp player's lateral position to stay within road bounds
    playerCar.current.x = Math.max(0, Math.min(GAME_WIDTH - PLAYER_CAR_WIDTH, playerCar.current.x));
  }, [gameState]);

  const handleTouchStart = useCallback((direction) => {
    // For continuous movement on touch hold, we might use setInterval here
    // For simplicity, we'll just trigger the move once per touch start.
    // More advanced would be to set a flag in useRef and clear on touchEnd.
    handleTouchMove(direction);
  }, [handleTouchMove]);

  // --- Render UI ---
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black-900 font-mono text-white p-4 relative overflow-hidden">
      {/* Dynamic Background Effect */}
      <div className="absolute inset-0 z-0 bg-stars animate-bg-pan"></div>

      <h1 className="text-5xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600 drop-shadow-lg animate-pulse-slow relative z-10">
        HYPER RACER 2D
      </h1>

      <div className="flex items-center justify-center relative z-10">
        {/* Left Touch Control */}
        <div className="hidden sm:flex flex-col justify-center h-full mr-4">
          <button
            onTouchStart={() => handleTouchStart('up')}
            onMouseDown={() => keysPressed.current['ArrowUp'] = true}
            onMouseUp={() => keysPressed.current['ArrowUp'] = false}
            onMouseLeave={() => keysPressed.current['ArrowUp'] = false}
            className="w-16 h-16 mb-2 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center text-3xl shadow-lg active:scale-95 transition-transform"
          >
            ⬆️
          </button>
          <button
            onTouchStart={() => handleTouchStart('left')}
            onMouseDown={() => keysPressed.current['ArrowLeft'] = true}
            onMouseUp={() => keysPressed.current['ArrowLeft'] = false}
            onMouseLeave={() => keysPressed.current['ArrowLeft'] = false}
            className="w-16 h-16 bg-gradient-to-br from-green-600 to-green-800 rounded-full flex items-center justify-center text-3xl shadow-lg active:scale-95 transition-transform"
          >
            ⬅️
          </button>
        </div>

        <div className="relative border-4 border-cyan-500 rounded-2xl shadow-xl overflow-hidden bg-gray-800 bg-opacity-70" style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}>
          <canvas
            ref={canvasRef}
            width={GAME_WIDTH}
            height={GAME_HEIGHT}
            className="block"
          ></canvas>

          {/* Overlay UI elements (Score, Speed, Lives, Fuel, Multiplier, Pause Button) */}
          {gameState === GAME_STATE.PLAYING && (
            <>
              <div className="absolute top-4 left-4 bg-gray-700 bg-opacity-75 px-4 py-2 rounded-lg shadow-md text-xl flex flex-col">
                <span>Score: <span className="text-yellow-400 font-bold">{score}</span></span>
                <span>x<span className="text-pink-400 font-bold">{scoreMultiplier.toFixed(1)}</span></span>
              </div>
              <div className="absolute top-4 right-4 bg-gray-700 bg-opacity-75 px-4 py-2 rounded-lg shadow-md text-xl">
                Speed: <span className="text-green-400 font-bold">{Math.floor(playerCar.current.speed * 10)} MPH</span>
              </div>
              <div className="absolute bottom-4 left-4 bg-gray-700 bg-opacity-75 px-4 py-2 rounded-lg shadow-md text-xl flex items-center">
                Lives: <span className="text-red-400 font-bold ml-2">{'❤️'.repeat(lives)}</span>
              </div>
              {/* Fuel Bar */}
              <div className="absolute bottom-4 right-4 bg-gray-700 bg-opacity-75 px-4 py-2 rounded-lg shadow-md text-xl">
                <span className="mr-2">Fuel:</span>
                <div className="w-24 h-4 bg-gray-600 rounded-full overflow-hidden inline-block align-middle">
                  <div
                    className="h-full rounded-full transition-all duration-300 ease-out"
                    style={{
                      width: `${fuel}%`,
                      backgroundColor: fuel > 50 ? '#4CAF50' : fuel > 20 ? '#FFC107' : '#F44336' // Green, Yellow, Red
                    }}
                  ></div>
                </div>
              </div>
              <button
                onClick={() => setGameState(GAME_STATE.PAUSED)}
                className="absolute top-4 left-1/2 -translate-x-1/2 bg-gray-700 bg-opacity-75 px-4 py-2 rounded-lg shadow-md text-lg text-white hover:bg-gray-600 transition-colors"
              >
                ⏸️ Pause
              </button>
            </>
          )}

          {/* Menu Screen */}
          {gameState === GAME_STATE.MENU && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-80 p-4">
              <p className="mb-8 text-cyan-200 text-center text-lg leading-relaxed">
                Use <span className="font-bold text-yellow-300">↑ ↓</span> or <span className="font-bold text-yellow-300">W S</span> to accelerate/brake.<br />
                Use <span className="font-bold text-yellow-300">← →</span> or <span className="font-bold text-yellow-300">A D</span> to steer.<br />
                Avoid other vehicles, collect boosts!
              </p>
              <button
                onClick={initializeGame}
                className="px-8 py-4 bg-gradient-to-r from-purple-700 to-cyan-600 rounded-xl font-extrabold text-white text-2xl shadow-lg hover:opacity-90 transition-all transform hover:scale-105 active:scale-95"
              >
                START RACE
              </button>
            </div>
          )}

          {/* Pause Screen */}
          {gameState === GAME_STATE.PAUSED && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-80 p-4">
              <h2 className="text-4xl font-extrabold mb-4 text-orange-400 animate-pulse">PAUSED</h2>
              <p className="text-xl mb-8 text-gray-300">Press 'Escape' or click 'Resume' to continue.</p>
              <button
                onClick={() => setGameState(GAME_STATE.PLAYING)}
                className="px-8 py-4 bg-gradient-to-r from-green-600 to-teal-500 rounded-xl font-extrabold text-white text-2xl shadow-lg hover:opacity-90 transition-all transform hover:scale-105 active:scale-95"
              >
                RESUME
              </button>
              <button
                onClick={() => setGameState(GAME_STATE.MENU)}
                className="mt-4 px-8 py-4 bg-gradient-to-r from-red-600 to-pink-500 rounded-xl font-extrabold text-white text-2xl shadow-lg hover:opacity-90 transition-all transform hover:scale-105 active:scale-95"
              >
                QUIT TO MENU
              </button>
            </div>
          )}

          {/* Game Over Screen */}
          {gameState === GAME_STATE.GAME_OVER && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-80 p-4">
              <h2 className="text-4xl font-extrabold mb-4 text-red-500 animate-pulse">CRASHED!</h2>
              <p className="text-3xl mb-2">Final Score: <span className="text-yellow-400 font-bold">{score}</span></p>
              <p className="text-2xl mb-8">High Score: <span className="text-purple-400 font-bold">{highScore}</span></p>
              <button
                onClick={initializeGame}
                className="px-8 py-4 bg-gradient-to-r from-purple-700 to-cyan-600 rounded-xl font-extrabold text-white text-2xl shadow-lg hover:opacity-90 transition-all transform hover:scale-105 active:scale-95"
              >
                RETRY
              </button>
            </div>
          )}
        </div>

        {/* Right Touch Control */}
        <div className="hidden sm:flex flex-col justify-center h-full ml-4">
          <button
            onTouchStart={() => handleTouchStart('down')}
            onMouseDown={() => keysPressed.current['ArrowDown'] = true}
            onMouseUp={() => keysPressed.current['ArrowDown'] = false}
            onMouseLeave={() => keysPressed.current['ArrowDown'] = false}
            className="w-16 h-16 mb-2 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center text-3xl shadow-lg active:scale-95 transition-transform"
          >
            ⬇️
          </button>
          <button
            onTouchStart={() => handleTouchStart('right')}
            onMouseDown={() => keysPressed.current['ArrowRight'] = true}
            onMouseUp={() => keysPressed.current['ArrowRight'] = false}
            onMouseLeave={() => keysPressed.current['ArrowRight'] = false}
            className="w-16 h-16 bg-gradient-to-br from-green-600 to-green-800 rounded-full flex items-center justify-center text-3xl shadow-lg active:scale-95 transition-transform"
          >
            ➡️
          </button>
        </div>
      </div>

      <div className="mt-10 text-gray-400 text-sm text-center relative z-10">
        <p>&copy; 2025 Hyper Racer. Created for an immersive experience.</p>
      </div>

      {/* Tailwind CSS keyframes for custom animations and background */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s infinite ease-in-out;
        }

        /* Starry background effect */
        .bg-stars {
          background: #000000;
          background-image:
            radial-gradient(white, rgba(255,255,255,.2) 2px, transparent 40px),
            radial-gradient(white, rgba(255,255,255,.15) 1px, transparent 30px),
            radial-gradient(white, rgba(255,255,255,.1) 2px, transparent 40px),
            radial-gradient(rgba(255,255,255,.4), rgba(255,255,255,.1) 2px, transparent 30px);
          background-size: 500px 500px, 300px 300px, 400px 400px, 200px 200px;
          background-position: 0 0, 100px 60px, 200px 120px, 300px 180px;
          animation: bg-pan 300s linear infinite; /* Slower pan */
        }

        @keyframes bg-pan {
          from { background-position: 0% 0%; }
          to { background-position: 100% 100%; }
        }

        /* Hide touch controls on desktop, show on mobile/tablet */
        @media (min-width: 640px) { /* sm breakpoint */
          .hidden.sm\\:flex {
            display: flex !important;
          }
        }
        @media (max-width: 639px) {
          .hidden.sm\\:flex {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default RacingGame;
